import React, { FunctionComponent, useEffect, useState } from 'react'
import { Button, Card, Select, Typography } from 'antd'
import SpinLoader from '../../../helpers/SpinLoader'
import { useCallContext } from '../../../../context/call/CallProvider'

import { useCameraTest } from './hooks/useCameraTest'
import useDevices from './hooks/useDevices'
import useAudioTest from './hooks/useAudioTest'
import ProgressBar from './ProgressBar/ProgressBar'
import { Mic, Volume2 } from 'react-feather'
import { TMediaConstraints } from '../../../../context/types'
import './index.less'
import { AudioFilled, SoundFilled, VideoCameraFilled } from '@ant-design/icons'

type TProps = {
	connectToCall: (constraints: TMediaConstraints) => void
	constraints: TMediaConstraints
}

const DeviceSettings: FunctionComponent<TProps> = ({ connectToCall, constraints }) => {
	const { videoElementRef, startVideoTest, stopVideoTest, videoTestError } = useCameraTest()
	const { videoInputDevices, audioInputDevices, audioOutputDevices } = useDevices()
	const {
		error,
		isRecording,
		isAudioOutputTestRunning,
		playAudio,
		playbackURI,
		readAudioInput,
		stopAudioTest,
		inputLevel,
		outputLevel,
	} = useAudioTest()

	const { call } = useCallContext()
	const [videoInputDeviceID, setVideoInputDeviceID] = useState<string>('')
	const [audioInputDeviceID, setAudioInputDeviceID] = useState<string>('')
	const [audioOutputDeviceID, setAudioOutputDeviceID] = useState<string>('')

	useEffect(() => {
		return () => {
			stopVideoTest()
			stopAudioTest()
		}
	}, [])

	useEffect(() => {
		const hasSelectedVideoInputDevice = videoInputDevices.some((device) => device.deviceId === videoInputDeviceID)
		const hasSelectedAudioInputDevice = audioInputDevices.some((device) => device.deviceId === audioInputDeviceID)
		const hasSelectedAudioOutputDevice = audioOutputDevices.some((device) => device.deviceId === audioOutputDeviceID)
		if (constraints.video && videoInputDevices.length && !hasSelectedVideoInputDevice) {
			setVideoInputDeviceID(videoInputDevices[0].deviceId)
		}
		if (constraints.audio && audioInputDevices.length && !hasSelectedAudioInputDevice) {
			setAudioInputDeviceID(audioInputDevices[0].deviceId)
		}
		if (audioOutputDevices.length && !hasSelectedAudioOutputDevice) {
			setAudioOutputDeviceID(audioOutputDevices[0].deviceId)
		}
	}, [videoInputDevices, audioInputDevices, audioOutputDevices])

	useEffect(() => {
		if (videoInputDeviceID) {
			startVideoTest(videoInputDeviceID)
		}
		if (videoTestError) {
			stopVideoTest()
		}
	}, [videoInputDeviceID])

	useEffect(() => {
		if (audioInputDeviceID) {
			readAudioInput({ deviceId: audioInputDeviceID, enableRecording: false })
		}
	}, [audioInputDeviceID])

	const volumeLevel = isAudioOutputTestRunning ? outputLevel : inputLevel

	const disableAll = isRecording || isAudioOutputTestRunning || (!!error && error !== 'No audio detected')

	const handleRecordClick = () => {
		readAudioInput({ deviceId: audioInputDeviceID, enableRecording: true })
	}

	const handlePlayClick = () => {
		playAudio({ deviceId: audioOutputDeviceID, testURI: playbackURI })
	}

	const connectToCallWithDevices = () => {
		connectToCall({
			audio: audioInputDeviceID ? { deviceId: audioInputDeviceID } : false,
			video: videoInputDeviceID ? { deviceId: videoInputDeviceID } : false,
		})
	}

	return (
		<SpinLoader loading={!!call.loading} className="lobby-wrapper">
			<Card title="Check Your device settings" className="device-settings">
				<div className="device-settings_container">
					<div className="camera-container">
						<div className="camera-wrapper">
							<video ref={videoElementRef} playsInline style={{ transform: 'scaleX(-1)', width: '100%' }} />
							{(!videoInputDevices.length || !!videoTestError) && <span className="no-camera">No camera detected</span>}
						</div>
						{!!constraints.audio && !!audioInputDevices.length && <ProgressBar position={volumeLevel} duration={0.1} />}
					</div>

					<div className="controls-container">
						{!!constraints?.video && !!videoInputDevices.length && (
							<div className="device-setting camera-select">
								<VideoCameraFilled style={{ fontSize: 20, color: '#95c6e4' }} />

								<Select bordered={false} onChange={(value) => setVideoInputDeviceID(value)} value={videoInputDeviceID}>
									{videoInputDevices.map((device) => (
										<Select.Option key={device.deviceId} value={device.deviceId}>
											{device.label}
										</Select.Option>
									))}
								</Select>
							</div>
						)}
						{!!constraints.audio && !!audioInputDevices.length && (
							<div className="device-setting micro-select">
								<AudioFilled style={{ fontSize: 20, color: '#95c6e4' }} />

								<Select bordered={false} onChange={(value) => setAudioInputDeviceID(value)} value={audioInputDeviceID}>
									{audioInputDevices.map((device) => (
										<Select.Option key={device.deviceId} value={device.deviceId}>
											{device.label}
										</Select.Option>
									))}
								</Select>
							</div>
						)}
						{!!audioOutputDevices.length && !!audioInputDevices.length && (
							<div className="device-setting speaker-select">
								<div className="speaker-select-container">
									<SoundFilled style={{ fontSize: 20, color: '#95c6e4' }} />

									<Select
										bordered={false}
										onChange={(value) => setAudioOutputDeviceID(value)}
										value={audioOutputDeviceID}
										className="speaker-selectbox"
									>
										{audioOutputDevices.map((device) => (
											<Select.Option key={device.deviceId} value={device.deviceId}>
												{device.label}
											</Select.Option>
										))}
									</Select>
								</div>

								<div className="speaker-test">
									<Typography>
										<strong>Test speaker</strong>
									</Typography>
									<div className="btn-group">
										<Button
											style={{ marginRight: '1em', display: 'flex', alignItems: 'center' }}
											shape="round"
											size="small"
											disabled={disableAll}
											onClick={handleRecordClick}
										>
											<div style={{ width: '2em', display: 'flex', justifyContent: 'center' }}>
												{isAudioOutputTestRunning ? <Volume2 /> : <Mic />}
											</div>

											<span>Record</span>
										</Button>
										<Button size="small" shape="round" disabled={!playbackURI || disableAll} onClick={handlePlayClick}>
											Play back
										</Button>
									</div>
								</div>
							</div>
						)}
					</div>
				</div>

				<Button type="primary" shape="round" size="large" className="join-btn" onClick={connectToCallWithDevices}>
					Join Call
				</Button>
			</Card>
		</SpinLoader>
	)
}

export default DeviceSettings
